import {
  Component,
  OnInit,
  ViewEncapsulation,
  OnDestroy,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
  signal,
  computed,
} from '@angular/core';
import { Store } from '@ngrx/store';

import { delayFor } from 'app/core/utilities/async-utils';

import { BrowserService } from '../../services/browser.service';
import { PopOutMode, socialAppActions } from '../../state/app';

@Component({
  selector: 'app-presentation-popout-button',
  templateUrl: './presentation-popout-button.component.html',
  styleUrls: ['./presentation-popout-button.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PresentationPopoutButtonComponent implements OnInit, OnDestroy {
  private store = inject(Store);

  public readonly startedPopout = output<number>();
  public readonly endedPopout = output<null>();
  public readonly videoElementToPopOut = input<HTMLVideoElement>();
  public readonly mode = input<PopOutMode>('NONE');
  protected readonly windowPopOut = signal(false);
  protected isFirefox = false;

  public constructor() {
    const browserService = inject(BrowserService);

    this.isFirefox = browserService.isFirefox;
  }

  public ngOnInit() {
    this.checkPictureInPicture();
  }

  public ngOnDestroy() {
    this.videoElementToPopOut()?.removeEventListener('leavepictureinpicture', this.leavePictureInPicture);
    this.videoElementToPopOut()?.removeEventListener('enterpictureinpicture', this.enterPictureInPicture);
  }

  protected startPictureInPicture() {
    const videoElementToPopOut = this.videoElementToPopOut();
    if (videoElementToPopOut && this.isPiPEnable()) {
      videoElementToPopOut
        .requestPictureInPicture()
        .catch(() => {
          this.store.dispatch(socialAppActions.UPDATE_POP_OUT({ mode: this.mode(), open: false }));
          this.windowPopOut.set(false);
        })
        .finally(async () => {
          await delayFor(500);
          this.startedPopout.emit(Date.now());
        });
    }
  }

  protected stopPictureInPicture() {
    if (document.pictureInPictureElement) {
      document
        .exitPictureInPicture()
        .catch((err) => new Error(err))
        .finally(() => {
          this.endedPopout.emit(null);
        });
    }
  }

  private leavePictureInPicture() {
    this.windowPopOut.set(false);
    this.endedPopout.emit(null);
    this.store.dispatch(socialAppActions.UPDATE_POP_OUT({ mode: this.mode(), open: false }));
  }

  private async enterPictureInPicture() {
    this.windowPopOut.set(true);
    // wait animation to start
    await delayFor(250);
    this.store.dispatch(socialAppActions.UPDATE_POP_OUT({ mode: this.mode(), open: true }));
  }

  private checkPictureInPicture() {
    this.videoElementToPopOut()?.addEventListener('leavepictureinpicture', this.leavePictureInPicture);
    this.videoElementToPopOut()?.addEventListener('enterpictureinpicture', this.enterPictureInPicture);
  }

  protected readonly isPiPEnable = computed(
    () => document.pictureInPictureEnabled && !this.isFirefox && !!this.videoElementToPopOut(),
  );
}
